import React from "react";
import clsx from "clsx";

type Column<T> = {
  key: string;
  header: string;
  accessor?: keyof T;
  className?: string;
  hiddenOn?: "sm" | "lg"; // Extendable
  render?: (row: T) => React.ReactNode;
};

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  className?: string;
}

export function DataTable<T extends object>({
  data,
  columns,
  rowKey,
  onRowClick,
  className,
}: DataTableProps<T>) {
  return (
    <table className={clsx("w-full min-w-full", className)}>
      <thead>
        <tr className="border-b border-gray-200">
          {columns.map((col) => (
            <th
              key={col.key}
              className={clsx(
                "text-left py-3 px-2 sm:px-4 font-medium text-gray-700 text-sm",
                col.hiddenOn === "sm" && "hidden sm:table-cell",
                col.hiddenOn === "lg" && "hidden lg:table-cell",
                col.className
              )}
            >
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row) => (
          <tr
            key={rowKey(row)}
            onClick={() => onRowClick?.(row)}
            className={clsx(
              "border-b border-gray-100 hover:bg-gray-50",
              onRowClick && "cursor-pointer"
            )}
          >
            {columns.map((col) => (
              <td
                key={col.key}
                className={clsx(
                  "py-3 px-2 sm:px-4 text-sm text-gray-700",
                  col.hiddenOn === "sm" && "hidden sm:table-cell",
                  col.hiddenOn === "lg" && "hidden lg:table-cell",
                  col.className
                )}
              >
                {col.render ? col.render(row) : (row[col.accessor as keyof T] as any) ?? "-"}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
